import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, Empty, message } from 'antd';
import { GridSkeleton } from '../components/Skeleton';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { LotDetailContent } from './LotDetail';
import Pagination from '../components/Pagination';
import useBackRestore, { useScrollRestore } from '../hooks/useBackRestore';
import { LOT_CATEGORIES } from '../config/lotCategories';
import dayjs from 'dayjs';

const GOLD = '#B8892A';
const BD = '#D8D2C8';
const BD2 = '#EAE6DF';
const BD3 = '#F0EDE8';
const BG = '#FAF8F5';
const BG2 = '#F4F1EC';
const BG3 = '#EDEAE3';
const INK = '#1A1612';
const INK2 = '#3D3830';
const INK3 = '#7A7268';
const INK4 = '#B0A898';
const RED = '#9B2335';
const GRN = '#2A6645';
const BLUE = '#2B5CE6';

const JPY_CNY_FALLBACK = 0.043;

// 抽屉输入框样式 —— 移到模块顶部(原在文件底部,组件渲染中引用属"声明前使用",
// 会被 terser 压成 "Cannot access before initialization" 的 TDZ 白屏)
const drawerInputStyle = {
  flex: 1, padding: '10px 12px', border: `1px solid ${BD}`, borderRadius: 8,
  fontSize: 14, color: INK, outline: 'none', background: '#fff', boxSizing: 'border-box',
};

const STATUS_LABEL = {
  live: { text: '竞拍中', color: RED, bg: 'rgba(155,35,53,.1)' },
  preview: { text: '预览中', color: BLUE, bg: 'rgba(43,92,230,.1)' },
  upcoming: { text: '即将开拍', color: GOLD, bg: 'rgba(184,137,42,.12)' },
  settling: { text: '结算中', color: GRN, bg: 'rgba(42,102,69,.1)' },
  ended: { text: '已结束', color: INK4, bg: BG3 },
};

function formatJPY(val) {
  if (val == null) return '--';
  const n = Number(val);
  return n >= 10000 ? `${(n / 10000).toFixed(1)}万` : `¥${n.toLocaleString()}`;
}

function useCountdown(endAt) {
  const [text, setText] = useState('');
  const [longText, setLongText] = useState('');
  const [urgent, setUrgent] = useState(false);
  useEffect(() => {
    if (!endAt) { setText(''); setLongText(''); return; }
    const update = () => {
      const diff = dayjs(endAt).diff(dayjs(), 'second');
      if (diff <= 0) { setText('已结束'); setLongText('已结束'); setUrgent(false); return; }
      setUrgent(diff < 3 * 3600);
      const d = Math.floor(diff / 86400), h = Math.floor((diff % 86400) / 3600),
        m = Math.floor((diff % 3600) / 60), s = diff % 60;
      if (d > 0) setText(`${d}天${h}h`);
      else if (h > 0) setText(`${h}h${String(m).padStart(2, '0')}m`);
      else setText(`${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
      if (d > 0) setLongText(`${d}天${h}小时${m}分钟`);
      else if (h > 0) setLongText(`${h}小时${m}分钟${s}秒`);
      else setLongText(`${m}分钟${s}秒`);
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [endAt]);
  return { text, longText, urgent };
}

const isEffectivelyEnded = (lot) =>
  lot.status === 'ended' || lot.status === 'closed' ||
  (lot.end_at && dayjs(lot.end_at).isBefore(dayjs()));

/* ── Lot Card ── */
function LotCard({ lot, isWatchlisted, isBidding, onToggleWatchlist, onOpenDetail, jpyCnyRate }) {
  const { t } = useTranslation();
  const { text: countdown, longText: countdownLong, urgent } = useCountdown(lot.end_at);
  let images = [];
  try { images = typeof lot.image_urls === 'string' ? JSON.parse(lot.image_urls) : (lot.image_urls || []); } catch { }
  const imgSrc = images[0] || null;
  const currentPrice = lot.current_price_jpy || lot.start_price_jpy || 0;
  const startPrice = lot.start_price_jpy || 0;
  const cnyCurrent = Math.round(currentPrice * jpyCnyRate);
  const ended = isEffectivelyEnded(lot);

  return (
    <div
      onClick={() => onOpenDetail(lot)}
      style={{
        background: '#fff', border: `1px solid ${BD2}`, borderRadius: 8,
        overflow: 'hidden', cursor: 'pointer', transition: 'all 0.15s', position: 'relative',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(26,20,16,.08)'; e.currentTarget.style.borderColor = GOLD; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = BD2; }}
    >
      {/* Image */}
      <div style={{ aspectRatio: '1', background: BG3, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
        {imgSrc ? (
          <img src={imgSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
        ) : (
          <span style={{ fontSize: 44, color: INK4 }}>📦</span>
        )}
        {/* Grade badge */}
        {lot.grade && (
          <div style={{
            position: 'absolute', top: 6, right: 6, fontSize: 10, fontWeight: 700,
            padding: '2px 6px', borderRadius: 3,
            background: 'rgba(255,255,255,.92)', color: INK2, border: `1px solid ${BD}`,
          }}>{lot.grade}</div>
        )}
        {/* Bidding badge */}
        {isBidding && !ended && (
          <div style={{
            position: 'absolute', top: 6, left: 6, fontSize: 9, padding: '2px 6px',
            background: RED, color: '#fff', borderRadius: 3, fontWeight: 700,
          }}>竞拍中</div>
        )}
        {isBidding && ended && lot.mandate_won && (
          <div style={{
            position: 'absolute', top: 6, left: 6, fontSize: 9, padding: '2px 6px',
            background: 'rgba(34,139,34,.88)', color: '#fff', borderRadius: 3, fontWeight: 700,
          }}>竞得</div>
        )}
        {/* Countdown */}
        {countdown && !ended && (
          <div style={{
            position: 'absolute', bottom: 5, left: 5, fontFamily: 'var(--mono,monospace)', fontSize: 9,
            padding: '2px 6px', borderRadius: 3,
            background: urgent ? 'rgba(155,35,53,.85)' : 'rgba(26,20,16,.72)', color: '#fff',
          }}>⏱ {countdown}</div>
        )}
        {/* Watchlist */}
        <div style={{
          position: 'absolute', bottom: 5, right: 5, width: 24, height: 24, borderRadius: '50%',
          background: 'rgba(255,255,255,.88)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, cursor: 'pointer', color: isWatchlisted ? RED : INK4,
        }}
          onClick={e => { e.stopPropagation(); onToggleWatchlist(lot.id); }}
        >{isWatchlisted ? '★' : '☆'}</div>
      </div>
      {/* Body */}
      <div style={{ padding: '8px 10px 10px' }}>
        {/* Title */}
        <div style={{
          fontSize: 12, fontWeight: 500, color: INK, lineHeight: 1.4, marginBottom: 4,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          minHeight: 34,
        }}>{lot.title_zh || lot.title_ja || '--'}</div>
        {/* Lot number */}
        <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: 10, color: INK4, marginBottom: 6 }}>
          编号:{lot.box_no && lot.item_no
            ? `${lot.box_no}-${String(lot.item_no).padStart(2, '0')}`
            /* 无 item_no 时优先用唯一的 external_lot_id;裸 box_no(如timeless"W61")一箱多件会重复,别当编号 */
            : (lot.external_lot_id || lot.box_no || lot.item_no || lot.lot_number || '--')}
        </div>
        {/* Prices — 暗拍密封竞价只显示起拍价（不公开当前价，避免误导） */}
        {lot.bid_type === 'sealed' ? (
          <div style={{ fontSize: 13, fontWeight: 600, color: RED, fontFamily: 'var(--mono,monospace)' }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: '#8C5A0A', background: 'rgba(140,90,10,.1)', border: '1px solid rgba(140,90,10,.25)', borderRadius: 3, padding: '1px 4px', marginRight: 5, fontFamily: 'sans-serif', verticalAlign: 'middle' }}>🔒暗拍</span>
            起拍价:{formatJPY(startPrice)}
            <span style={{ fontSize: 10, fontWeight: 400, color: INK4, marginLeft: 4 }}>≈¥{Math.round(startPrice * jpyCnyRate).toLocaleString()}</span>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 11, color: INK3, marginBottom: 2 }}>
              起拍价:<span style={{ fontFamily: 'var(--mono,monospace)', color: INK2 }}>{formatJPY(startPrice)}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: RED, fontFamily: 'var(--mono,monospace)' }}>
              当前价:{formatJPY(currentPrice)}
              <span style={{ fontSize: 10, fontWeight: 400, color: INK4, marginLeft: 4 }}>≈¥{cnyCurrent.toLocaleString()}</span>
            </div>
          </>
        )}
        {/* Countdown text */}
        {countdownLong && !ended && (
          <div style={{ fontSize: 10, color: urgent ? RED : INK3, marginTop: 4, fontFamily: 'var(--mono,monospace)' }}>
            ⏱ {countdownLong}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Lot Detail Dialog ── */
function LotDetailDialog({ lotId, titleJa, onClose, onBidPlaced }) {
  const { t } = useTranslation();
  const overlayRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  if (!lotId) return null;

  if (isMobile) {
    return (
      <div ref={overlayRef} onClick={e => { if (e.target === overlayRef.current) onClose(); }}
        style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(26,22,18,.55)', backdropFilter: 'blur(3px)' }}>
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: BG, borderRadius: '16px 16px 0 0',
          height: '87vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 -8px 40px rgba(26,22,18,.22)', overflow: 'hidden',
        }}>
          <div style={{ flexShrink: 0, background: '#fff', borderBottom: `1px solid ${BD2}` }}>
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 6 }}>
              <div style={{ width: 40, height: 4, borderRadius: 2, background: '#C8C0B4' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', padding: '0 16px 12px', gap: 10 }}>
              <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: INK, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {titleJa || t('lotDetail.title')}
              </div>
              <button onClick={onClose} style={{
                flexShrink: 0, width: 36, height: 36, border: 'none', borderRadius: '50%',
                background: INK, cursor: 'pointer', fontSize: 18, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>✕</button>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <LotDetailContent lotId={lotId} isDialog={true} onBidPlaced={onBidPlaced} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={overlayRef} onClick={e => { if (e.target === overlayRef.current) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(26,22,18,.55)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 16px' }}>
      <div style={{
        background: BG, borderRadius: 10, width: '100%', maxWidth: 1100,
        height: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 80px rgba(26,22,18,.28)', overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 12px', flexShrink: 0, borderBottom: `1px solid ${BD2}` }}>
          <button onClick={onClose} style={{ width: 32, height: 32, border: `1px solid ${BD}`, borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 18, color: INK3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <LotDetailContent lotId={lotId} isDialog={true} onBidPlaced={onBidPlaced} />
        </div>
      </div>
    </div>
  );
}

/* ── Sort tab button ── */
// Maps UI key → backend sort value pairs [asc, desc]
const SORT_MAP = {
  start_price: ['start_price_jpy:asc', 'start_price_jpy:desc'],
  grade: ['box_no', 'box_no'],  // grade sort not supported, fallback to box_no
  end_at: ['end_at:asc', 'end_at:desc'],
};
const SORT_TABS = [
  { key: 'start_price', label: '起拍价' },
  { key: 'grade', label: '成色' },
  { key: 'end_at', label: '剩余时间' },
];

function parseSortKey(sort) {
  for (const [key, [asc, desc]] of Object.entries(SORT_MAP)) {
    if (sort === asc) return { key, dir: 'asc' };
    if (sort === desc) return { key, dir: 'desc' };
  }
  return { key: '', dir: 'asc' };
}

function SortTabs({ sort, onToggle }) {
  const { key: activeKey, dir } = parseSortKey(sort);
  return (
    <>
      {SORT_TABS.map(tab => {
        const active = activeKey === tab.key;
        const arrow = active ? (dir === 'desc' ? ' ↓' : ' ↑') : ' ↕';
        return (
          <button key={tab.key} onClick={() => onToggle(tab.key)}
            style={{
              padding: '6px 12px', border: 'none', borderRadius: 0, background: 'transparent',
              fontSize: 13, fontWeight: active ? 600 : 400, cursor: 'pointer',
              color: active ? RED : INK3,
            }}
          >{tab.label}{arrow}</button>
        );
      })}
    </>
  );
}

/* ── Collapsible section header ── */
function SectionHeader({ title, open, onToggle, count }) {
  return (
    <button onClick={onToggle} style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%',
      padding: '0 0 10px', border: 'none', background: 'transparent', cursor: 'pointer',
    }}>
      <span style={{ fontSize: 15, fontWeight: 600, color: INK }}>
        {title}{count > 0 && <span style={{ fontSize: 11, color: BLUE, marginLeft: 6 }}>({count})</span>}
      </span>
      <span style={{ fontSize: 14, color: INK4, transition: 'transform .2s', transform: open ? 'rotate(0)' : 'rotate(180deg)' }}>∧</span>
    </button>
  );
}

/* ── Pill grid (multi-select) ── */
function PillGrid({ items, selected, onToggle, cols = 3 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 8 }}>
      {items.map(item => {
        const val = typeof item === 'string' ? item : item.value;
        const label = typeof item === 'string' ? item : item.label;
        const active = selected.includes(val);
        return (
          <button key={val} onClick={() => onToggle(val)} style={{
            padding: '8px 4px', border: `1px solid ${active ? BLUE : BD}`, borderRadius: 6,
            background: active ? 'rgba(43,92,230,.08)' : '#fff',
            color: active ? BLUE : INK2, fontSize: 12, fontWeight: active ? 600 : 400,
            cursor: 'pointer', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{label}</button>
        );
      })}
    </div>
  );
}

const BRAND_LIST = [
  // 箱包
  { value: 'LOUIS VUITTON', label: '路易威登' },
  { value: 'HERMES', label: '爱马仕' },
  { value: 'CHANEL', label: '香奈儿' },
  { value: 'GUCCI', label: '古驰' },
  { value: 'PRADA', label: '普拉达' },
  { value: 'FENDI', label: '芬迪' },
  { value: 'DIOR', label: '迪奥' },
  { value: 'COACH', label: '蔻驰' },
  { value: 'BURBERRY', label: '巴宝莉' },
  { value: 'CELINE', label: '思琳' },
  { value: 'LOEWE', label: '罗意威' },
  { value: 'SAINT LAURENT', label: '圣罗兰' },
  { value: 'MIU MIU', label: '缪缪' },
  { value: 'BALENCIAGA', label: '巴黎世家' },
  { value: 'VALENTINO', label: '华伦天奴' },
  { value: 'BOTTEGA VENETA', label: '葆蝶家' },
  { value: 'GIVENCHY', label: '纪梵希' },
  { value: 'FERRAGAMO', label: '菲拉格慕' },
  { value: 'VERSACE', label: '范思哲' },
  { value: 'CHLOE', label: '克洛伊' },
  { value: 'GOYARD', label: '戈雅' },
  // 钟表
  { value: 'ROLEX', label: '劳力士' },
  { value: 'OMEGA', label: '欧米茄' },
  { value: 'LONGINES', label: '浪琴' },
  { value: 'PATEK PHILIPPE', label: '百达翡丽' },
  { value: 'AUDEMARS PIGUET', label: '爱彼' },
  { value: 'VACHERON CONSTANTIN', label: '江诗丹顿' },
  { value: 'IWC', label: '万国' },
  { value: 'JAEGER-LECOULTRE', label: '积家' },
  { value: 'BREITLING', label: '百年灵' },
  { value: 'PANERAI', label: '沛纳海' },
  { value: 'TAG HEUER', label: '泰格豪雅' },
  { value: 'TUDOR', label: '帝舵' },
  { value: 'HUBLOT', label: '宇舶' },
  { value: 'SEIKO', label: '精工' },
  // 珠宝
  { value: 'CARTIER', label: '卡地亚' },
  { value: 'BVLGARI', label: '宝格丽' },
  { value: 'TIFFANY', label: '蒂芙尼' },
  { value: 'VAN CLEEF & ARPELS', label: '梵克雅宝' },
  { value: 'HARRY WINSTON', label: '海瑞温斯顿' },
  { value: 'PIAGET', label: '伯爵' },
  { value: 'MIKIMOTO', label: '御木本' },
  { value: 'CHOPARD', label: '萧邦' },
];

// 品牌原始值 → 中文标签查表（动态品牌项用它取显示名，命中则中文，否则原样）
const BRAND_MAP = Object.fromEntries(BRAND_LIST.map(b => [b.value.toUpperCase(), b.label]));
function brandLabel(raw) {
  if (!raw) return raw;
  return BRAND_MAP[String(raw).toUpperCase()] || raw;
}

// 一级分类统一走共享源(与 Browse 顶部tab/筛选一致,避免"手表/腕表""箱包/名包"名称打架)
const CATEGORY_LIST = LOT_CATEGORIES.map(c => ({ value: c.value, label: c.zh }));

const FULL_GRADES = ['SA', 'S', 'A', 'AB', 'B+', 'B', 'B-', 'BC', 'C', 'D', 'E', 'F', 'J', 'N', 'P'];

/* ── Filter Drawer (right-side) ── */
function FilterDrawer({
  open, onClose,
  brandOptions,
  categoryOptions,
  brands, setBrands,
  grades, setGrades,
  categories, setCategories,
  priceMin, setPriceMin, priceMax, setPriceMax,
  startPriceMin, setStartPriceMin, startPriceMax, setStartPriceMax,
  buynowMin, setBuynowMin, buynowMax, setBuynowMax,
  onApply, onReset,
}) {
  const overlayRef = useRef(null);
  const [sections, setSections] = useState({ brand: true, grade: true, price: true, start: true, buynow: false, category: true });
  const toggle = (key) => setSections(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleList = (setter, val) => setter(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  return (
    <div ref={overlayRef} onClick={e => { if (e.target === overlayRef.current) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(26,22,18,.45)' }}>
      <div style={{
        position: 'absolute', top: 0, right: 0, bottom: 0, width: Math.min(340, window.innerWidth * 0.82),
        background: '#fff', display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 24px rgba(26,22,18,.15)',
        animation: 'slideInRight .2s ease-out',
      }}>
        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 18px 100px' }}>

          {/* 一级分类（置顶，便于按品类定位）——恒显；有本场真实分类则动态展示(带数量,单类也显示)，
              加载中/接口失败回退固定四类。分类已在入库时按标题分类回填，各拍卖会均有分类可选。 */}
          <div style={{ marginBottom: 20 }}>
            <SectionHeader title="一级分类" open={sections.category} onToggle={() => toggle('category')} count={categories.length} />
            {sections.category && (
              <PillGrid
                items={categoryOptions && categoryOptions.length ? categoryOptions : CATEGORY_LIST}
                selected={categories} onToggle={v => toggleList(setCategories, v)} cols={3}
              />
            )}
          </div>

          {/* 品牌 */}
          <div style={{ marginBottom: 20 }}>
            <SectionHeader title="品牌" open={sections.brand} onToggle={() => toggle('brand')} count={brands.length} />
            {sections.brand && (
              brandOptions == null
                ? <div style={{ fontSize: 12, color: INK4, padding: '4px 2px' }}>加载本场品牌…</div>
                : brandOptions.length
                  ? <PillGrid items={brandOptions} selected={brands} onToggle={v => toggleList(setBrands, v)} cols={3} />
                  : <div style={{ fontSize: 12, color: INK4, padding: '4px 2px' }}>本场次暂无品牌拍品</div>
            )}
          </div>

          {/* 成色 */}
          <div style={{ marginBottom: 20 }}>
            <SectionHeader title="成色" open={sections.grade} onToggle={() => toggle('grade')} count={grades.length} />
            {sections.grade && <PillGrid items={FULL_GRADES} selected={grades} onToggle={v => toggleList(setGrades, v)} cols={5} />}
          </div>

          {/* 当前价 */}
          <div style={{ marginBottom: 20 }}>
            <SectionHeader title="当前价" open={sections.price} onToggle={() => toggle('price')} count={(priceMin || priceMax) ? 1 : 0} />
            {sections.price && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="number" min="0" value={priceMin} onChange={e => setPriceMin(e.target.value)}
                  placeholder="最低价" style={drawerInputStyle} />
                <span style={{ color: INK4 }}>—</span>
                <input type="number" min="0" value={priceMax} onChange={e => setPriceMax(e.target.value)}
                  placeholder="最高价" style={drawerInputStyle} />
              </div>
            )}
          </div>

          {/* 起拍价 */}
          <div style={{ marginBottom: 20 }}>
            <SectionHeader title="起拍价" open={sections.start} onToggle={() => toggle('start')} count={(startPriceMin || startPriceMax) ? 1 : 0} />
            {sections.start && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="number" min="0" value={startPriceMin} onChange={e => setStartPriceMin(e.target.value)}
                  placeholder="最低价" style={drawerInputStyle} />
                <span style={{ color: INK4 }}>—</span>
                <input type="number" min="0" value={startPriceMax} onChange={e => setStartPriceMax(e.target.value)}
                  placeholder="最高价" style={drawerInputStyle} />
              </div>
            )}
          </div>

          {/* 一口价：暂时下架 — estimate_high_jpy 全库无数据(2026-07-03 实测 27万件 0 条非空)，
              筛选恒为空结果的死按钮。等哪家源站真提供一口价数据后再恢复。 */}

        </div>

        {/* Bottom bar */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, padding: '12px 18px',
          borderTop: `1px solid ${BD2}`, background: '#fff', display: 'flex', gap: 12,
          paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
        }}>
          <button onClick={onReset} style={{
            flex: 1, padding: '12px 0', border: `1px solid ${BD}`, borderRadius: 8,
            background: '#fff', color: INK3, fontSize: 15, fontWeight: 500, cursor: 'pointer',
          }}>重置</button>
          <button onClick={onApply} style={{
            flex: 1, padding: '12px 0', border: 'none', borderRadius: 8,
            background: BLUE, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
          }}>确定</button>
        </div>
      </div>
      <style>{`@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Main: SessionLots
   ══════════════════════════════════════════════════════════════════ */
export default function SessionLots() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { sessionId } = useParams();

  // 返回恢复:进登录/委托/详情路由页等真实跳转后 back 回来,组件重挂载 —— 从快照恢复
  // 页码/排序/关键词/筛选/滚动,而不是重置回第1页顶部(「返回被弹回起始点」的另一半根因)。
  const [restored, saveListState] = useBackRestore();

  const [session, setSession] = useState(null);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(() => restored?.page || 1);
  const [pageSize, setPageSize] = useState(() => restored?.pageSize || 50);
  const [sort, setSort] = useState(() => restored?.sort || 'box_no');
  const [keyword, setKeyword] = useState(() => restored?.keyword || '');
  const [searchInput, setSearchInput] = useState(() => restored?.keyword || '');
  const [jpyCnyRate, setJpyCnyRate] = useState(JPY_CNY_FALLBACK);
  const [dialogLot, setDialogLot] = useState(null);

  // 详情是 modal(非路由)。原来不推历史 → 手机/浏览器返回键会直接离开列表页退回上级/首页,
  // 而不是关掉 modal 留在列表(用户翻到第2/3页点进商品后返回被弹回首页的根因)。
  // 修:modal 打开时 pushState 一条历史;返回键触发 popstate → 关 modal 且消费掉这条历史,停在列表原位。
  useEffect(() => {
    if (!dialogLot) return;
    window.history.pushState({ lotModal: true }, '');
    const onPop = () => setDialogLot(null);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [dialogLot]);

  // Filters
  const [brandFilter, setBrandFilter] = useState(() => restored?.brandFilter || []);
  const [brandOptions, setBrandOptions] = useState(null); // 本场次实际含有的品牌（null=未加载）
  const [categoryOptions, setCategoryOptions] = useState(null); // 本场次实际存在的分类（null=未加载/失败回退全量）
  const [gradeFilter, setGradeFilter] = useState(() => restored?.gradeFilter || []);
  const [categoryFilter, setCategoryFilter] = useState(() => restored?.categoryFilter || []);
  const [priceMin, setPriceMin] = useState(() => restored?.priceMin || '');
  const [priceMax, setPriceMax] = useState(() => restored?.priceMax || '');
  const [startPriceMin, setStartPriceMin] = useState(() => restored?.startPriceMin || '');
  const [startPriceMax, setStartPriceMax] = useState(() => restored?.startPriceMax || '');
  const [buynowMin, setBuynowMin] = useState(() => restored?.buynowMin || '');
  const [buynowMax, setBuynowMax] = useState(() => restored?.buynowMax || '');
  const [filterOpen, setFilterOpen] = useState(false);

  // 状态一变就写快照(sessionStorage);滚动位置由 useScrollRestore 持续写入
  useEffect(() => {
    saveListState({
      page, pageSize, sort, keyword,
      brandFilter, gradeFilter, categoryFilter,
      priceMin, priceMax, startPriceMin, startPriceMax, buynowMin, buynowMax,
    });
  }, [saveListState, page, pageSize, sort, keyword, brandFilter, gradeFilter, categoryFilter,
    priceMin, priceMax, startPriceMin, startPriceMax, buynowMin, buynowMax]);
  useScrollRestore(restored, saveListState, !lotsLoading && lots.length > 0);

  // Watchlist
  const [watchlistedIds, setWatchlistedIds] = useState(new Set());
  const [biddingIds, setBiddingIds] = useState(new Set());

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Countdown for session
  const { longText: sessionCountdown, urgent: sessionUrgent } = useCountdown(session?.end_at);

  // Fetch session info
  useEffect(() => {
    if (!sessionId) return;
    api.get(`/sessions/${sessionId}`).then(({ data }) => {
      setSession(data.data || data || null);
    }).catch(() => {
      message.error('加载场次信息失败');
    }).finally(() => setLoading(false));
  }, [sessionId]);

  // 品牌 / 分类的 facet 是重查询(按整场 GROUP BY),且只用于「筛选」抽屉。
  // 延后到用户首次打开筛选时才拉(每场次只拉一次)——避免每次进场次都在首屏跑两条重 GROUP BY，
  // 「品牌多的大场次」开屏明显更快。
  const brandsFetchedFor = useRef(null);
  const catsFetchedFor = useRef(null);
  useEffect(() => {
    if (!sessionId || !filterOpen) return;
    if (brandsFetchedFor.current !== sessionId) {
      brandsFetchedFor.current = sessionId;
      setBrandOptions(null);
      api.get('/lots/brands', { params: { session_id: sessionId } })
        .then(({ data }) => {
          const list = data.data || data || [];
          setBrandOptions(list.map(b => ({ value: b.brand, label: b.label || brandLabel(b.brand) })));
        })
        .catch(() => { setBrandOptions([]); brandsFetchedFor.current = null; });
    }
    if (catsFetchedFor.current !== sessionId) {
      catsFetchedFor.current = sessionId;
      setCategoryOptions(null);
      api.get('/lots/categories', { params: { session_id: sessionId } })
        .then(({ data }) => {
          const list = data.data || [];
          const labelOf = (v) => (CATEGORY_LIST.find(c => c.value === v) || {}).label || v;
          setCategoryOptions(list.map(c => ({ value: c.category, label: `${labelOf(c.category)} (${c.count})` })));
        })
        .catch(() => { setCategoryOptions(null); catsFetchedFor.current = null; });
    }
  }, [sessionId, filterOpen]);

  // Fetch exchange rate
  useEffect(() => {
    api.get('/exchange-rates/current').then(({ data }) => {
      const rate = data?.rate || data?.conservative_rate;
      if (rate && rate > 0) setJpyCnyRate(Number(rate));
    }).catch(() => {});
  }, []);

  // Fetch watchlist
  useEffect(() => {
    api.get('/watchlist').then(({ data }) => {
      const ids = (data.data || data || []).map(w => w.id);
      setWatchlistedIds(new Set(ids));
    }).catch(() => {});
  }, []);

  // Fetch lots
  const fetchLots = useCallback(async () => {
    if (!sessionId) return;
    setLotsLoading(true);
    try {
      const params = { page, page_size: pageSize, session_id: sessionId };
      if (sort) params.sort = sort;
      if (keyword) params.keyword = keyword;
      if (brandFilter.length) params.brands = brandFilter.join(',');
      if (gradeFilter.length) params.grades = gradeFilter.join(',');
      if (categoryFilter.length) params.categories = categoryFilter.join(',');
      if (priceMin) params.price_min = priceMin;
      if (priceMax) params.price_max = priceMax;
      if (startPriceMin) params.start_min = startPriceMin;
      if (startPriceMax) params.start_max = startPriceMax;
      if (buynowMin) params.buynow_min = buynowMin;
      if (buynowMax) params.buynow_max = buynowMax;
      const { data } = await api.get('/lots', { params });
      const newLots = data.data || [];
      setBiddingIds(new Set(newLots.filter(l => l.has_active_mandate).map(l => l.id)));
      setLots(newLots);
      setTotal(data.total || 0);
    } catch { }
    finally { setLotsLoading(false); }
  }, [sessionId, page, pageSize, sort, keyword, brandFilter, gradeFilter, categoryFilter, priceMin, priceMax, startPriceMin, startPriceMax, buynowMin, buynowMax]);

  useEffect(() => { fetchLots(); }, [fetchLots]);

  // Auto-refresh every 3 min
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState !== 'hidden') fetchLots();
    }, 180_000);
    return () => clearInterval(timer);
  }, [fetchLots]);

  const toggleWatchlist = async (lotId) => {
    if (!user) { message.info('请先登录'); navigate('/login'); return; }
    if (watchlistedIds.has(lotId)) {
      try {
        await api.delete(`/watchlist/${lotId}`);
        setWatchlistedIds(prev => { const next = new Set(prev); next.delete(lotId); return next; });
      } catch { message.error('操作失败'); }
    } else {
      try {
        await api.post('/watchlist', { lot_id: lotId });
        setWatchlistedIds(prev => new Set(prev).add(lotId));
      } catch { message.error('操作失败'); }
    }
  };

  const handleSearch = () => {
    setKeyword(searchInput.trim());
    setPage(1);
  };

  const st = session ? (STATUS_LABEL[session.status] || STATUS_LABEL.ended) : null;

  if (loading) {
    return <GridSkeleton count={8} />;
  }

  if (!session) {
    return (
      <div style={{ padding: '80px 24px', textAlign: 'center', background: BG, minHeight: '100vh' }}>
        <Empty description="场次不存在或已删除" />
        <button onClick={() => navigate('/auction-hub')} style={{ marginTop: 16, padding: '8px 24px', border: `1px solid ${BD}`, borderRadius: 6, background: '#fff', cursor: 'pointer', color: INK3 }}>返回拍场总览</button>
      </div>
    );
  }

  return (
    <div style={{ background: BG, minHeight: 'calc(100vh - 120px)' }}>
      {/* ── Session header ── */}
      <div style={{
        background: '#fff', borderBottom: `1px solid ${BD2}`,
        padding: isMobile ? '12px 16px 14px' : '16px 32px 18px',
        position: 'sticky', top: isMobile ? 0 : 60, zIndex: 100,
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {/* Back + title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <button onClick={() => navigate(-1)} style={{
              width: 32, height: 32, border: `1px solid ${BD}`, borderRadius: 6,
              background: '#fff', cursor: 'pointer', fontSize: 16, color: INK3,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>←</button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: isMobile ? 15 : 18, fontWeight: 700, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {session.house_name || ''} · {session.name || '拍品列表'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, flexWrap: 'wrap' }}>
                {st && (
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: st.bg, color: st.color }}>{st.text}</span>
                )}
                {session.bid_type && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                    ...(session.bid_type === 'open'
                      ? { background: 'rgba(42,102,69,.12)', color: '#2A6645', border: '1px solid rgba(42,102,69,.25)' }
                      : { background: 'rgba(140,90,10,.12)', color: '#8C5A0A', border: '1px solid rgba(140,90,10,.25)' }),
                  }}>
                    {session.bid_type === 'open' ? '明拍' : '🔒 暗拍'}
                  </span>
                )}
                <span style={{ fontSize: 11, color: INK3, fontFamily: 'var(--mono,monospace)' }}>
                  {total.toLocaleString()} 件
                </span>
                {sessionCountdown && session.status !== 'ended' && (
                  <span style={{ fontSize: 11, color: sessionUrgent ? RED : INK3, fontFamily: 'var(--mono,monospace)' }}>
                    剩余 {sessionCountdown}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Search bar — 2026-07-04: 手机(微信webview)键盘不出"搜索"键、也没可见按钮,
              用户输完没法触发搜索。改:form 包裹(提交即搜索)+enterKeyHint 让键盘显示
              「搜索」+ 补一个可见的搜索按钮。 */}
          <form
            onSubmit={e => { e.preventDefault(); handleSearch(); }}
            style={{ display: 'flex', gap: 8, marginBottom: 4 }}
          >
            <div style={{ flex: 1, position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: INK4, pointerEvents: 'none' }}>🔍</span>
              <input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                enterKeyHint="search"
                placeholder="搜索一下"
                style={{
                  width: '100%', padding: isMobile ? '8px 12px 8px 32px' : '9px 14px 9px 34px',
                  border: `1px solid ${BD}`, borderRadius: 8,
                  fontSize: 13, color: INK, outline: 'none', background: BG,
                  boxSizing: 'border-box',
                }}
              />
              {keyword && (
                <button type="button" onClick={() => { setKeyword(''); setSearchInput(''); setPage(1); }} style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  width: 20, height: 20, border: 'none', borderRadius: '50%', background: INK4,
                  color: '#fff', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 0, lineHeight: 1,
                }}>✕</button>
              )}
            </div>
            <button type="submit" style={{
              flexShrink: 0, padding: isMobile ? '8px 14px' : '9px 18px',
              border: 'none', borderRadius: 8, background: '#2f54eb', color: '#fff',
              fontSize: 13, cursor: 'pointer', lineHeight: 1.2,
            }}>搜索</button>
          </form>

          {/* Sort tabs + filter button */}
          <div style={{ display: 'flex', alignItems: 'center', borderTop: `1px solid ${BD3}`, marginTop: 4 }}>
            <SortTabs sort={sort} onToggle={key => {
              const { key: activeKey, dir } = parseSortKey(sort);
              const [asc, desc] = SORT_MAP[key];
              if (activeKey === key) {
                setSort(dir === 'asc' ? desc : asc);
              } else {
                setSort(asc);
              }
              setPage(1);
            }} />
            <div style={{ flex: 1 }} />
            {(() => {
              const hasFilter = brandFilter.length > 0 || gradeFilter.length > 0 || categoryFilter.length > 0 || priceMin || priceMax || startPriceMin || startPriceMax || buynowMin || buynowMax;
              return (
                <button onClick={() => setFilterOpen(true)} style={{
                  padding: '6px 12px', border: 'none', borderRadius: 0, background: 'transparent',
                  fontSize: 13, fontWeight: hasFilter ? 600 : 400, cursor: 'pointer',
                  color: hasFilter ? RED : INK3, position: 'relative',
                }}>
                  筛选 ▼{hasFilter && <span style={{ color: RED, marginLeft: 2 }}>●</span>}
                </button>
              );
            })()}
          </div>
        </div>
      </div>

      {/* ── Lot grid ── */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '12px 12px 80px' : '20px 32px 40px' }}>
        <>
          {lotsLoading && lots.length === 0 ? (
            <GridSkeleton count={8} />
          ) : lots.length === 0 ? (
            <div style={{ padding: '60px 0', textAlign: 'center' }}>
              <Empty description={keyword ? '没有匹配的拍品' : '暂无拍品'} />
            </div>
          ) : (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: isMobile ? 8 : 14,
              }}>
                {lots.map(lot => (
                  <LotCard
                    key={lot.id}
                    lot={lot}
                    isWatchlisted={watchlistedIds.has(lot.id)}
                    isBidding={biddingIds.has(lot.id)}
                    onToggleWatchlist={toggleWatchlist}
                    onOpenDetail={l => setDialogLot({ id: l.id, titleJa: l.title_ja || l.title_zh })}
                    jpyCnyRate={jpyCnyRate}
                  />
                ))}
              </div>

              {/* Pagination — 千旗式数字分页 */}
              <Pagination
                page={page}
                pageSize={pageSize}
                total={total}
                currentCount={lots.length}
                onChange={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              />
            </>
          )}
        </>
      </div>

      {/* Lot detail dialog */}
      {dialogLot && (
        <LotDetailDialog
          lotId={dialogLot.id}
          titleJa={dialogLot.titleJa}
          onClose={() => window.history.back()}
          onBidPlaced={() => fetchLots()}
        />
      )}

      {/* Filter drawer */}
      <FilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        brandOptions={brandOptions}
        categoryOptions={categoryOptions}
        brands={brandFilter}
        setBrands={setBrandFilter}
        grades={gradeFilter}
        setGrades={setGradeFilter}
        categories={categoryFilter}
        setCategories={setCategoryFilter}
        priceMin={priceMin}
        setPriceMin={setPriceMin}
        priceMax={priceMax}
        setPriceMax={setPriceMax}
        startPriceMin={startPriceMin}
        setStartPriceMin={setStartPriceMin}
        startPriceMax={startPriceMax}
        setStartPriceMax={setStartPriceMax}
        buynowMin={buynowMin}
        setBuynowMin={setBuynowMin}
        buynowMax={buynowMax}
        setBuynowMax={setBuynowMax}
        onApply={() => { setPage(1); setFilterOpen(false); }}
        onReset={() => {
          setBrandFilter([]); setGradeFilter([]); setCategoryFilter([]);
          setPriceMin(''); setPriceMax('');
          setStartPriceMin(''); setStartPriceMax('');
          setBuynowMin(''); setBuynowMax('');
          setPage(1); setFilterOpen(false);
        }}
      />
    </div>
  );
}

